import React, { useRef, useState } from 'react';
import Grid from "@material-ui/core/Grid";
import {
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    TextField,
} from "@material-ui/core";
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AuthenticationService from "../../Services/AuthenticationService.js";
import { validateTinSyntax } from '../../utils/Validator.js';
import LoadingMsgBar from "../LoadingMsgBar.js";
import AddUserTinListTable from "./AddUserTinListTable.js";

const FTIN_ERROR_MESSAGES = {
    'INVALID_FTIN_IN_NETWORK': 'Error: This TIN cannot be added because it is registered as In-network TIN.  Only non-network TINs are permitted for non-network user.',
    'INVALID_FTIN_NO_BILLS_FOUND': 'Error: This TIN cannot be added because no bills exist in the system. Only TINs with existing bills can be associated with user account.',
    'FTIN_RESTRICTED_FOR_REGISTRATION': 'Error: Adding a TIN owned by our organization is not allowed.  Select a different TIN and resubmit.',
    'INVALID_FTIN_NOT_IN_NETWORK': 'Error: This TIN is not in-network. Only in-network TINs can be added.',
};

/**
 * Self-contained TIN input section shared by PRAD (new group) and NNUS account types.
 *
 * Props:
 *   validationMode  - 'inNetwork' (PRAD) | 'nonNetwork' (NNUS)
 *   onTinsChanged   - callback(tins[]) called whenever the TIN list changes (add/remove)
 *   externalError   - optional error string set by parent-level form validation (e.g. "must add at least one TIN")
 *   allowForceAdd   - when true (ISAD users), renders a split button exposing the FORCE-ADD option
 */
export default function AddUserTinSection({ validationMode, onTinsChanged, externalError, allowForceAdd }) {
    const [tin, setTin] = useState('');
    const [tinError, setTinError] = useState('');
    const [tins, setTinsState] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [isAddEnabled, setIsAddEnabled] = useState(false);
    const [busyMsg, setBusyMsg] = useState('');
    const [splitOpen, setSplitOpen] = useState(false);
    const splitAnchorRef = useRef(null);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setTin(value);
        setIsAddEnabled(value.trim() !== '');
        setTinError('');
    };

    const handleValidationError = (errorData) => {
        if (!errorData || !errorData.message) {
            setTinError('Error: Unable to validate TIN. Please try again.');
            return;
        }
        setTinError(FTIN_ERROR_MESSAGES[errorData.message] || 'Error: Unable to validate TIN. Please try again.');
    };

    const callValidateService = (tinValue) => {
        setBusyMsg('Please wait while we validate the TIN.');
        const serviceCall = validationMode === 'inNetwork'
            ? AuthenticationService.validateInNetworkFTIN(tinValue)
            : AuthenticationService.validateNonNetworkFTIN(tinValue);

        return serviceCall.then((response) => {
            setBusyMsg('');
            if (response.status === 200) return true;
            if (response.response?.status === 400) {
                handleValidationError(response.response.data);
                return false;
            }
            handleValidationError(null);
            return false;
        }).catch((error) => {
            setBusyMsg('');
            handleValidationError(error.response?.data);
            console.log(`Validate Ftin ${error.message || error}`);
            return false;
        });
    };

    // Shared syntax check + duplicate check; returns the trimmed TIN or null on failure.
    const validateBeforeAdd = (trimmedTin) => {
        setTinError('');
        const syntaxError = validateTinSyntax(trimmedTin);
        if (syntaxError) {
            setTinError(syntaxError);
            return false;
        }
        if (tins.some(t => t.ftin === trimmedTin)) {
            setTinError('You already added this TIN');
            return false;
        }
        return true;
    };

    const commitTin = (trimmedTin) => {
        const newEntry = { ftin: trimmedTin, prvId: trimmedTin };
        const updated = [...tins, newEntry];
        setTinsState(updated);
        setFiltered(updated);
        setTin('');
        setIsAddEnabled(false);
        onTinsChanged(updated);
    };

    const handleAdd = () => {
        const trimmedTin = tin.trim();
        if (!validateBeforeAdd(trimmedTin)) return;

        callValidateService(trimmedTin).then((isValid) => {
            if (isValid) commitTin(trimmedTin);
        });
    };

    const handleForceAdd = () => {
        setSplitOpen(false);
        const trimmedTin = tin.trim();
        if (!validateBeforeAdd(trimmedTin)) return;
        // Skip backend validation — add immediately after frontend checks pass.
        commitTin(trimmedTin);
    };

    const handleSplitToggle = () => setSplitOpen(prev => !prev);

    const handleSplitClose = (event) => {
        if (splitAnchorRef.current && splitAnchorRef.current.contains(event.target)) return;
        setSplitOpen(false);
    };

    const handleDelete = (index) => {
        const updated = [...tins];
        updated.splice(index, 1);
        setTinsState(updated);
        setFiltered(updated);
        onTinsChanged(updated);
    };

    const handleFilterChange = (newFiltered) => {
        if (newFiltered && newFiltered.length >= 0) {
            setFiltered(newFiltered);
        } else {
            setFiltered([]);
        }
    };

    const displayError = tinError || externalError || '';

    return (
        <>
            <LoadingMsgBar loadMsg={busyMsg} />
            <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
                <Grid item xs>
                    <TextField
                        id="tinInput"
                        name="tinInput"
                        label="Tax ID Number (TIN)"
                        value={tin}
                        error={!!displayError}
                        helperText={displayError || '\u00A0'}
                        onChange={handleInputChange}
                        inputProps={{ maxLength: 9 }}
                        fullWidth
                        required
                    />
                </Grid>
                <Grid item style={{ paddingTop: '20px' }}>
                    {allowForceAdd ? (
                        <>
                            <ButtonGroup variant="outlined" color="primary" ref={splitAnchorRef} disabled={!isAddEnabled} style={{ marginTop: '0.5rem' }}>
                                <Button onClick={handleAdd} style={{ margin: 0 }}>Add</Button>
                                <Button size="small" aria-label="select add option" aria-haspopup="menu"
                                    onClick={handleSplitToggle} style={{ margin: 0 }}>
                                    <ArrowDropDownIcon />
                                </Button>
                            </ButtonGroup>
                            <Popper open={splitOpen} anchorEl={splitAnchorRef.current}
                                role={undefined} transition disablePortal style={{ zIndex: 1 }}>
                                {({ TransitionProps, placement }) => (
                                    <Grow {...TransitionProps}
                                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
                                        <Paper>
                                            <ClickAwayListener onClickAway={handleSplitClose}>
                                                <MenuList>
                                                    <MenuItem onClick={handleForceAdd}>Force-Add</MenuItem>
                                                </MenuList>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>
                        </>
                    ) : (
                        <Button variant="outlined" onClick={handleAdd} disabled={!isAddEnabled}>
                            Add
                        </Button>
                    )}
                </Grid>
            </Grid>
            {tins.length > 0 && (
                <AddUserTinListTable
                    rows={tins}
                    filtered={filtered}
                    setFilter={handleFilterChange}
                    deleteRow={handleDelete}
                />
            )}
        </>
    );
}
